import { createEmptyHistoryState, registerHistory } from '@lexical/history';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  registerList,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { registerRichText } from '@lexical/rich-text';
import { mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createRangeSelection,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  createEditor,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  TextNode,
  UNDO_COMMAND,
  type CreateEditorArgs,
  type EditorThemeClasses,
  type LexicalEditor,
  type NodeKey,
  type TextFormatType,
} from 'lexical';

import type { TextBlock } from '@/teoCore/TeoApi';

import { TeoEventTarget } from '../../../../../teoCore/utility/TeoEventTarget';
import type { EditorMarkerManager, TeoMarker } from '../EditorMarkerManager.svelte';
import { $docxExport } from './teoLexicalDocxExport';
import {
  $currentSelectionStartsInNodeType,
  $getLeafNodes,
  $getPlainText,
  $getTeoTextContent,
  $getTextArray,
  $transformMarkerToOffsetMarker,
} from './teoLexicalUtility';
import { $isTeoTextNode, TeoTextNode } from './TeoTextNode';

type EditorContentEventDetailMap = {
  contentChanged: (detail: {
    content: string;
    getTextArray: () => TextBlock[];
    serialized: string;
  }) => void;
  // editorUpdated: () => void;
  formattingChanged: (serialized: string) => void;
  canUndo: (canUndo: boolean) => void;
  canRedo: (canRedo: boolean) => void;
};

export default class EditorContent {
  editor: LexicalEditor;
  textNodes: { [key: string | number]: NodeKey[] } = {}; // key: markerId, value: text node key
  config: CreateEditorArgs = {
    namespace: 'test',
    nodes: [
      // HeadingNode,
      // QuoteNode,
      TeoTextNode,
      ListNode,
      ListItemNode,
      {
        replace: TextNode,
        with: (node: TextNode) => new TeoTextNode(this, [], node.__text),
        withKlass: TeoTextNode,
      },
    ],
    onError: (error: Error) => {
      throw error;
    },
    theme: {},
  };
  events = new TeoEventTarget<EditorContentEventDetailMap>();

  constructor(
    private root: HTMLElement,
    namespace: string,
    private markers: EditorMarkerManager | null = null,
    theme?: EditorThemeClasses,
  ) {
    this.config.namespace = namespace;
    if (theme) {
      this.config.theme = theme;
    }
    this.editor = createEditor(this.config);
    this.editor.setRootElement(this.root);
    mergeRegister(
      registerRichText(this.editor),
      registerHistory(this.editor, createEmptyHistoryState(), 300),
      registerList(this.editor),
    );
    // this.editor.registerUpdateListener(() => {
    //   this.events.triggerEvent('editorUpdated', undefined);
    // });
    this.editor.registerTextContentListener(() => {
      this.events.triggerEvent('contentChanged', {
        content: this.getRootTextContent(),
        getTextArray: () => this.getTextArray(),
        serialized: this.getSerialized(),
      });
    });
    new ResizeObserver(() => markers?.sizeChangeUpdate()).observe(root);
    this.registerUndoRedo();
    this.listenToCommands();
  }

  disable() {
    this.editor.setEditable(false);
  }

  enable() {
    this.editor.setEditable(true);
  }

  // showMarker(markerType: string) {
  //   this.showMarkers([markerType]);
  // }

  addTextNode(markerId: string, nodeKey: NodeKey) {
    if (this.textNodes[markerId] === undefined) {
      this.textNodes[markerId] = [];
    }
    if (this.textNodes[markerId].includes(nodeKey)) return;
    this.textNodes[markerId].push(nodeKey);
  }

  removeTextNode(markerId: string, nodeKey: NodeKey) {
    if (this.textNodes[markerId] === undefined) return;
    const index = this.textNodes[markerId].indexOf(nodeKey);
    if (index !== -1) {
      this.textNodes[markerId].splice(index, 1);
    }
    if (this.textNodes[markerId].length === 0) {
      delete this.textNodes[markerId];
    }
  }

  getTextElements(markerId: string): HTMLElement[] {
    if (this.textNodes[markerId] === undefined || this.textNodes[markerId].length === 0) {
      return [];
    }
    const elements: HTMLElement[] = [];
    const nodeKeys: NodeKey[] = [];
    this.textNodes[markerId].forEach((nodeKey) => {
      const node = this.editor.getElementByKey(nodeKey);
      if (node === null) return;
      const element = this.editor.getElementByKey(nodeKey);
      if (element === null) return;
      elements.push(element);
      nodeKeys.push(nodeKey);
    });
    // cleanup
    this.textNodes[markerId] = nodeKeys;
    return elements;
  }

  getRootTextContent() {
    return this.editor.read(() => {
      return $getTeoTextContent($getRoot());
    });
  }

  getText() {
    let text: string = '';
    this.editor.read(() => {
      text = $getPlainText($getRoot());
    });
    return text;
  }

  // private splitTextBlockAtRange(
  //   textArray: TextBlock[],
  //   index: number,
  //   start: number,
  //   end: number,
  // ): TextBlock[] {
  //   const block = textArray[index];
  //   if (!block) {
  //     throw new Error('Text block not found at index');
  //   }
  //   const clampedStart = Math.max(0, Math.min(start, block.string.length));
  //   const clampedEnd = Math.max(clampedStart, Math.min(end, block.string.length));
  //   if (clampedStart === clampedEnd) {
  //     return textArray;
  //   }
  //   if (block.length !== undefined && block.length !== block.string.length) {
  //     throw new Error('Cannot split text block with custom non-string length.');
  //   }

  //   const before = block.string.substring(0, clampedStart);
  //   const middle = block.string.substring(clampedStart, clampedEnd);
  //   const after = block.string.substring(clampedEnd);

  //   const createSegment = (segment: string): TextBlock => {
  //     return {
  //       ...block,
  //       string: segment,
  //       length: segment.length,
  //     };
  //   };
  //   const replacementBlocks: TextBlock[] = [];
  //   if (before.length > 0) replacementBlocks.push(createSegment(before));
  //   if (middle.length > 0) replacementBlocks.push(createSegment(middle));
  //   if (after.length > 0) replacementBlocks.push(createSegment(after));
  //   textArray.splice(index, 1, ...replacementBlocks);
  //   return textArray;
  // }

  getTextArray() {
    let textArray: TextBlock[] = [];
    this.editor.read(() => {
      textArray = $getTextArray($getRoot());
    });
    /**
     * The API counts (multibyte) characters, while JS counts UTF-16 code units.
     * We have to tell the API the length as JS sees it, to get the correct offsets in the responses.
     */
    // for (let i = textArray.length - 1; i >= 0; i--) {
    //   const block = textArray[i];
    //   if (block && block.string.length !== [...block.string].length) {
    //     if (block.type === 'text') {
    //       const multibyteRanges: { start: number; end: number }[] = [];
    //       let utf16Offset = 0;
    //       for (const character of block.string) {
    //         const nextOffset = utf16Offset + character.length;
    //         if (character.length > 1) {
    //           multibyteRanges.push({ start: utf16Offset, end: nextOffset });
    //         }
    //         utf16Offset = nextOffset;
    //       }
    //       for (let j = multibyteRanges.length - 1; j >= 0; j--) {
    //         const range = multibyteRanges[j];
    //         if (range !== undefined) {
    //           textArray = this.splitTextBlockAtRange(textArray, i, range.start, range.end);
    //         }
    //       }
    //     } else {
    //       block.length = block.string.length;
    //     }
    //   }
    // }
    // console.log('textArray', textArray);
    return textArray;
  }

  getSerialized() {
    const state = this.editor.getEditorState();
    return JSON.stringify(state);
  }

  replaceMarkerContent(markerId: string, content: string) {
    const nodeKeys = this.textNodes[markerId];
    let contentSet = false;
    this.editor.update(
      () => {
        if (nodeKeys === undefined) return;
        nodeKeys.forEach((nodeKey) => {
          const node = $getNodeByKey(nodeKey);
          if ($isTeoTextNode(node)) {
            node.setTextContent(contentSet ? '' : content);
            contentSet = true;
          }
        });
      },
      // { tag: 'history-merge' },
    );
  }

  addMarkers(markers: TeoMarker[], onUpdate?: () => void) {
    this.splitContentForMarkers(markers, () => {
      this.editor.update(
        () => {
          markers.forEach((marker) => {
            const offsetMarker = $transformMarkerToOffsetMarker(marker, $getRoot());
            if (offsetMarker === null) {
              console.warn('Invalid marker:', marker);
              return;
            }
            const selection = $createRangeSelection();
            selection.anchor.set(
              offsetMarker.start.node.getKey(),
              offsetMarker.start.offset,
              'text',
            );
            selection.focus.set(offsetMarker.end.node.getKey(), offsetMarker.end.offset, 'text');
            const affectedNodes = selection.getNodes();
            affectedNodes.forEach((node) => {
              if ($isTeoTextNode(node)) {
                node.addMarker(offsetMarker.marker);
              }
            });
            // const start = performance.now();
            // while (performance.now() - start < 10) {
            //   // busy-wait for 10ms
            // }
          });
        },
        {
          tag: 'history-merge',
          onUpdate,
        },
      );
    });
  }

  /**
   * Prepares the editor content for marker application.
   * Splits text nodes at marker boundaries to ensure markers align with node edges.
   *
   * @param markers - Array of TeoMarker objects defining marker ranges.
   * @param onUpdate - Optional callback invoked after the update.
   * @throws Error if a marker cannot be transformed to an offset marker.
   */
  private splitContentForMarkers(markers: TeoMarker[], onUpdate?: () => void) {
    this.editor.update(
      () => {
        const splits: { [key: string]: Set<number> } = {};
        markers.forEach((marker) => {
          const offsetMarker = $transformMarkerToOffsetMarker(marker, $getRoot());
          if (offsetMarker === null) {
            throw new Error('Invalid marker');
          }
          const startKey = offsetMarker.start.node.getKey();
          const endKey = offsetMarker.end.node.getKey();
          if (
            offsetMarker.start.offset > 0 &&
            offsetMarker.start.offset < offsetMarker.start.node.getTextContentSize()
          ) {
            if (!splits[startKey]) {
              splits[startKey] = new Set<number>();
            }
            splits[startKey].add(offsetMarker.start.offset);
          }
          if (
            offsetMarker.end.offset > 0 &&
            offsetMarker.end.offset < offsetMarker.end.node.getTextContentSize()
          ) {
            if (!splits[endKey]) {
              splits[endKey] = new Set<number>();
            }
            splits[endKey].add(offsetMarker.end.offset);
          }
        });
        for (const [nodeKey, offsets] of Object.entries(splits)) {
          const node = $getNodeByKey(nodeKey);
          if ($isTeoTextNode(node)) {
            const sortedOffsets = Array.from(offsets).sort((a, b) => b - a);
            for (const offset of sortedOffsets) {
              node.splitText(offset);
            }
          }
        }
      },
      {
        tag: 'history-merge',
        onUpdate: onUpdate,
        // discrete: true,
      },
    );
  }

  clearMarkers(markerIds?: string[]) {
    if (!markerIds || markerIds.length === 0) {
      this.textNodes = {};
      this.editor.update(() => {
        const root = $getRoot();
        $getLeafNodes(root).forEach((node) => {
          if ($isTeoTextNode(node)) {
            node.clearMarkers();
          }
        });
      });
    } else {
      markerIds.forEach((markerId) => {
        if (this.textNodes[markerId]) {
          delete this.textNodes[markerId];
        }
      });
      this.editor.update(() => {
        const root = $getRoot();
        $getLeafNodes(root).forEach((node) => {
          if ($isTeoTextNode(node)) {
            node.clearMarkers(markerIds);
          }
        });
      });
    }
  }

  importJSON(json: string, onUpdate?: () => void) {
    this.editor.update(
      () => {
        try {
          const state = this.editor.parseEditorState(json);
          this.editor.setEditorState(state);
        } catch (e) {
          console.error('Error importing JSON:', e);
        }
      },
      { onUpdate },
    );
  }

  addContent(content: string, onUpdate?: () => void) {
    this.editor.update(
      () => {
        const root = $getRoot();
        const paragraph = $createParagraphNode();
        paragraph.append($createTextNode(content));
        root.append(paragraph);
      },
      { onUpdate },
    );
  }

  setContent(content: string, onUpdate?: () => void) {
    this.editor.update(
      () => {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        paragraph.append($createTextNode(content));
        root.append(paragraph);
      },
      { onUpdate },
    );
  }

  onMarkerClick(markerId: number) {
    alert(`Marker clicked: ${markerId}`);
  }

  formatText(formant: TextFormatType) {
    this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, formant);
  }

  toggleUList() {
    const state = this.editor.getEditorState();
    state.read(() => {
      if ($currentSelectionStartsInNodeType('listitem')) {
        this.editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      } else {
        this.editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      }
    });
  }

  undo() {
    this.editor.dispatchCommand(UNDO_COMMAND, undefined);
  }

  redo() {
    this.editor.update(() => {
      this.editor.dispatchCommand(REDO_COMMAND, undefined);
    });
  }

  registerUndoRedo() {
    this.editor.registerCommand(
      CAN_UNDO_COMMAND,
      (e) => {
        this.events.triggerEvent('canUndo', e);
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
    this.editor.registerCommand(
      CAN_REDO_COMMAND,
      (e) => {
        this.events.triggerEvent('canRedo', e);
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }

  listenToCommands() {
    const commands = [
      FORMAT_TEXT_COMMAND,
      INSERT_UNORDERED_LIST_COMMAND,
      REMOVE_LIST_COMMAND,
      UNDO_COMMAND,
      REDO_COMMAND,
    ];
    commands.forEach((command) => {
      this.editor.registerCommand(
        command,
        () => {
          // Wait for the editor to update
          const unregister = this.editor.registerUpdateListener(() => {
            this.markers?.sizeChangeUpdate();
            this.events.triggerEvent('formattingChanged', this.getSerialized());
            unregister();
          });
          return false;
        },
        COMMAND_PRIORITY_LOW,
      );
    });
  }

  exportDocx(fileName: string = 'Teo_Export') {
    this.editor.read(() => {
      $docxExport($getRoot(), fileName);
    });
  }
}
